/**
 * @fileoverview Tenant store for multi-tenant context management
 * @module stores/tenant
 *
 * Uses centralized branding defaults for fallback values
 * @see src/config/brandingDefaults.ts
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { doc, getDoc, updateDoc, onSnapshot, collection, query, orderBy, getDocs } from 'firebase/firestore'
import { db } from '@/plugins/firebase'
import type { TenantProfile, TenantBranding, TenantSubscription, TenantUser, Invitation } from '@/types'
import { useAuthStore } from './auth'
import { IDENTITY_DEFAULTS } from '@/config/brandingDefaults'

/**
 * Tenant store managing current tenant context and data
 */
export const useTenantStore = defineStore('tenant', () => {
  const authStore = useAuthStore()

  // State
  const profile = ref<TenantProfile | null>(null)
  const branding = ref<TenantBranding | null>(null)
  const subscription = ref<TenantSubscription | null>(null)
  const teamMembers = ref<TenantUser[]>([])
  const pendingInvitations = ref<Invitation[]>([])
  const loading = ref(false)
  const teamLoading = ref(false)
  const error = ref<string | null>(null)

  // Subscriptions for cleanup
  let profileUnsubscribe: (() => void) | null = null
  let subscriptionUnsubscribe: (() => void) | null = null

  // Getters
  const tenantId = computed(() => authStore.currentTenantId)
  const subdomain = computed(() => authStore.currentSubdomain ?? profile.value?.subdomain)
  const tenantName = computed(() => branding.value?.companyName ?? profile.value?.name ?? IDENTITY_DEFAULTS.companyName)
  const isActive = computed(() => profile.value?.status === 'active')
  const isSuspended = computed(() => profile.value?.status === 'suspended')
  const isTrialing = computed(() => profile.value?.status === 'trialing')
  const planId = computed(() => subscription.value?.planId ?? 'free')
  const planStatus = computed(() => subscription.value?.status ?? 'active')

  // Enterprise features based on plan tier
  // Tier names aligned with /src/config/tiers.ts: free, base, pro, business, enterprise
  //
  // ASSURANCE-BASED PRICING MODEL:
  // All platform features are included on every paid plan - no feature gating.
  // Plans differentiate by: storage allocation, support responsiveness, and scale guidance.
  // Only enterprise-exclusive features (SSO, SCIM, SLA, compliance) are gated to Enterprise
  // tier because they represent contractual/compliance requirements, not core functionality.
  // This encourages trust and transparency while reserving enterprise features for
  // organizations with specific regulatory or operational needs.
  const enterpriseFeatures = computed(() => {
    const plan = planId.value
    const features: string[] = []

    // All plans get basic features and data export
    features.push('basic', 'dataExport')

    // All paid plans (Base and above) get all platform features
    if (['base', 'pro', 'business', 'enterprise'].includes(plan)) {
      features.push(
        'apiAccess',
        'advancedAnalytics',
        'whiteLabel',
        'auditLog',
        'webhooks',
        'customDomains'
      )
    }

    // Enterprise-only features (SSO, SCIM, SLA, compliance)
    if (plan === 'enterprise') {
      features.push('sso', 'scim', 'slaManagement', 'compliance', 'dedicatedSupport')
    }

    return features
  })

  /**
   * Check if tenant has a specific feature enabled
   */
  function hasFeature(featureId: string): boolean {
    return enterpriseFeatures.value.includes(featureId)
  }

  /**
   * Detect subdomain from current URL
   */
  function detectSubdomain(): string | null {
    const hostname = window.location.hostname
    const parts = hostname.split('.')

    // Handle localhost
    if (hostname.includes('localhost')) {
      return parts.length > 1 ? parts[0] : null
    }

    // domain.com (2 parts) -> no subdomain
    // acme.domain.com (3+ parts) -> 'acme'
    if (parts.length <= 2) {
      return null
    }

    const sub = parts[0].toLowerCase()
    // Skip reserved subdomains
    const reserved = ['www', 'app', 'api', 'admin']
    return reserved.includes(sub) ? null : sub
  }

  /**
   * Load tenant by subdomain from registry
   */
  async function loadBySubdomain(subdomain: string): Promise<string | null> {
    try {
      const registryDoc = await getDoc(doc(db, 'tenant_registry', subdomain))
      if (registryDoc.exists()) {
        const data = registryDoc.data()
        return data.tenantId
      }
      return null
    } catch {
      return null
    }
  }

  /**
   * Load tenant profile and subscribe to updates
   */
  async function loadProfile(id?: string): Promise<void> {
    const targetId = id ?? tenantId.value
    if (!targetId) {
      error.value = 'No tenant ID available'
      return
    }

    loading.value = true
    error.value = null

    try {
      // Unsubscribe from previous listener
      if (profileUnsubscribe) {
        profileUnsubscribe()
      }

      const profileRef = doc(db, `tenants/${targetId}/config/profile`)

      // Set up real-time listener
      profileUnsubscribe = onSnapshot(
        profileRef,
        (snapshot) => {
          if (snapshot.exists()) {
            profile.value = { id: targetId, ...snapshot.data() } as TenantProfile
          } else {
            profile.value = null
            error.value = 'Tenant not found'
          }
          loading.value = false
        },
        (_err) => {
          error.value = 'Failed to load tenant'
          loading.value = false
        }
      )
    } catch (err) {
      error.value = 'Failed to load tenant'
      loading.value = false
    }
  }

  /**
   * Load tenant branding (public, no auth required)
   */
  async function loadBranding(id?: string): Promise<void> {
    const targetId = id ?? tenantId.value
    if (!targetId) return

    try {
      const brandingDoc = await getDoc(doc(db, `tenants/${targetId}/config/branding`))
      if (brandingDoc.exists()) {
        branding.value = brandingDoc.data() as TenantBranding
      }
    } catch {
      // Branding load failed silently - will use defaults
    }
  }

  /**
   * Load branding from URL subdomain (for login page theming)
   */
  async function loadBrandingFromSubdomain(): Promise<void> {
    const sub = detectSubdomain()
    if (!sub) return

    const id = await loadBySubdomain(sub)
    if (id) {
      await loadBranding(id)
    }
  }

  /**
   * Load subscription and subscribe to updates
   */
  async function loadSubscription(id?: string): Promise<void> {
    const targetId = id ?? tenantId.value
    if (!targetId) return

    // Unsubscribe from previous listener
    if (subscriptionUnsubscribe) {
      subscriptionUnsubscribe()
    }

    const subscriptionRef = doc(db, `tenants/${targetId}/config/subscription`)

    subscriptionUnsubscribe = onSnapshot(
      subscriptionRef,
      (snapshot) => {
        if (snapshot.exists()) {
          subscription.value = snapshot.data() as TenantSubscription
        }
      },
      (_err) => {
        // Subscription listener error - handled silently
      }
    )
  }

  /**
   * Load team members for current tenant
   */
  async function loadTeamMembers(): Promise<void> {
    const id = tenantId.value
    if (!id) {
      error.value = 'No tenant ID available'
      return
    }

    teamLoading.value = true
    try {
      // Query users collection under tenant
      const usersRef = collection(db, `tenants/${id}/users`)
      const usersQuery = query(usersRef, orderBy('joinedAt', 'desc'))
      const snapshot = await getDocs(usersQuery)

      teamMembers.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TenantUser[]
    } catch {
      error.value = 'Failed to load team members'
    } finally {
      teamLoading.value = false
    }
  }

  /**
   * Load pending invitations for current tenant
   */
  async function loadPendingInvitations(): Promise<void> {
    const id = tenantId.value
    if (!id) return

    try {
      const invitationsRef = collection(db, `tenants/${id}/invitations`)
      const invitationsQuery = query(invitationsRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(invitationsQuery)

      pendingInvitations.value = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }) as Invitation)
        .filter(inv => inv.status === 'pending')
    } catch {
      // Invitations load failed silently
    }
  }

  /**
   * Update tenant branding
   */
  async function updateBranding(updates: Partial<TenantBranding>): Promise<void> {
    const id = tenantId.value
    if (!id) throw new Error('No tenant context')

    await updateDoc(doc(db, `tenants/${id}/config/branding`), updates)
    // Local state will update via listener or we can merge
    branding.value = { ...branding.value, ...updates } as TenantBranding
  }

  /**
   * Initialize tenant context
   */
  async function init(): Promise<void> {
    const id = tenantId.value
    if (!id) {
      // Try to load branding from URL for login page
      await loadBrandingFromSubdomain()
      return
    }

    await Promise.all([
      loadProfile(id),
      loadBranding(id),
      loadSubscription(id)
    ])
  }

  /**
   * Cleanup subscriptions
   */
  function cleanup(): void {
    if (profileUnsubscribe) {
      profileUnsubscribe()
      profileUnsubscribe = null
    }
    if (subscriptionUnsubscribe) {
      subscriptionUnsubscribe()
      subscriptionUnsubscribe = null
    }
    profile.value = null
    branding.value = null
    subscription.value = null
  }

  return {
    // State
    profile,
    branding,
    subscription,
    teamMembers,
    pendingInvitations,
    loading,
    teamLoading,
    error,
    // Getters
    tenantId,
    subdomain,
    tenantName,
    isActive,
    isSuspended,
    isTrialing,
    planId,
    planStatus,
    enterpriseFeatures,
    // Actions
    hasFeature,
    detectSubdomain,
    loadBySubdomain,
    loadProfile,
    loadBranding,
    loadBrandingFromSubdomain,
    loadSubscription,
    loadTeamMembers,
    loadPendingInvitations,
    updateBranding,
    init,
    cleanup
  }
})
